import { useState } from 'react';
import type { PrivateView, PublicView, SeerPickAction } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { FixedActionBar } from '../ui/FixedActionBar.js';
import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';

// Seer read colors — WOLF = crimson, clear = verdigris (both on-palette).
const READ_WOLF = 'var(--blood-6, #ff5a4f)';
const READ_CLEAR = 'var(--verdigris-3, #3d7a6c)';

/**
 * NightSeerScreen — seer's night action.
 * "Investigate one" + candidates + fixed confirm bar → private result reveal.
 * Persistent seer journal (collapsible) from seerHistory.
 */
export function NightSeerScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const action = privateView.action as SeerPickAction;
  const [selected, setSelected] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);

  const candidates = action.candidates;
  const nameMap = Object.fromEntries(publicView.players.map((p) => [p.id, p.name]));

  // Show result if we have one from a prior confirm
  const result = privateView.seerResult;

  function handleConfirm() {
    if (!selected) return;
    send({ type: 'SUBMIT_INVESTIGATE', targetId: selected });
    setConfirmed(true);
  }

  // After confirm, wait for the server to push back seerResult via PRIVATE_STATE.
  // We show a "Investigating…" state while waiting.
  if (confirmed && !result) {
    return (
      <div
        className="game-screen"
        style={{
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #000000 0%, #160b26 100%)',
          gap: 'var(--space-5)',
          padding: 'var(--space-5)',
          overflow: 'hidden',
        }}
      >
        <ParticleLayer preset="arcanaWisps" intensity={0.8} slot={0} />
        <div style={{ position: 'relative', zIndex: 1, animation: 'seer-pulse 1.5s ease-in-out infinite' }}>
          <Glyph name="seerEye" size={48} tone="arcana" />
        </div>
        <p
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: 'var(--font-display)',
            fontSize: 20,
            color: 'var(--text-secondary)',
            margin: 0,
            textAlign: 'center',
          }}
        >
          Peering into the dark…
        </p>
        <style>{`
          @keyframes seer-pulse {
            0%, 100% { opacity: 0.6; transform: scale(1); }
            50%       { opacity: 1; transform: scale(1.1); }
          }
          @media (prefers-reduced-motion: reduce) {
            @keyframes seer-pulse { 0%,100% { opacity: 0.85; transform: none; } }
          }
        `}</style>
      </div>
    );
  }

  // Result reveal (dramatic).
  if (result) {
    const isWolf = result.isWerewolf;
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: isWolf
            ? 'linear-gradient(180deg, #000000 0%, #320001 100%)'
            : 'linear-gradient(180deg, #000000 0%, #001a0a 100%)',
          gap: 'var(--space-5)',
          padding: 'var(--space-5)',
        }}
      >
        <Glyph name="seerEye" size={48} tone="arcana" />
        <div
          style={{
            textAlign: 'center',
            background: 'rgba(0,0,0,0.5)',
            border: `1px solid ${isWolf ? 'var(--blood-3, #802636)' : 'var(--verdigris-2, #1e4f47)'}`,
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-6)',
            maxWidth: 340,
            width: '100%',
          }}
        >
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 var(--space-3)' }}>
            Your vision reveals:
          </p>
          <p
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              margin: '0 0 var(--space-4)',
              color: 'var(--text-primary)',
            }}
          >
            {result.targetName} is&hellip;
          </p>
          {/* The verdict is pure color language: wolf-head in blood vs candle in gold. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 'var(--space-3)',
              animation: 'result-reveal 0.5s var(--ease-standard)',
            }}
          >
            <Glyph name={isWolf ? 'wolfHead' : 'candle'} size={44} tone={isWolf ? 'blood' : 'gold'} />
          </div>
          <p
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 30,
              margin: 0,
              color: isWolf ? READ_WOLF : READ_CLEAR,
              animation: 'result-reveal 0.5s 0.1s var(--ease-standard) both',
            }}
          >
            {isWolf ? 'a WEREWOLF!' : 'NOT a Werewolf'}
          </p>
        </div>

        {/* Seer journal */}
        {privateView.seerHistory && privateView.seerHistory.length > 0 && (
          <div style={{ width: '100%', maxWidth: 340 }}>
            <button
              type="button"
              className="rm-hover-row"
              onClick={() => setJournalOpen((o) => !o)}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--surface-stroke)',
                borderRadius: 14,
                padding: '12px var(--space-4)',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-ui)',
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Investigation Journal</span>
              <span>{journalOpen ? '▲' : '▼'}</span>
            </button>
            {journalOpen && (
              <div
                style={{
                  marginTop: 4,
                  background: 'rgba(0,0,0,0.5)',
                  border: '1px solid var(--surface-stroke)',
                  borderRadius: 14,
                  padding: 'var(--space-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-2)',
                }}
              >
                {privateView.seerHistory.map((entry) => (
                  <div
                    key={`${entry.round}-${entry.targetId}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <span>N{entry.round}: {entry.targetName}</span>
                    <span style={{ color: entry.isWerewolf ? READ_WOLF : READ_CLEAR, fontWeight: 600 }}>
                      {entry.isWerewolf ? 'WOLF' : 'clear'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <style>{`
          @keyframes result-reveal {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        // Arcana tint — mystic violet bleeding up out of the ink.
        background: 'linear-gradient(180deg, #080808 0%, #160b26 100%)',
        padding: 'var(--space-6) var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-6))',
        // Room for the fixed confirm bar (+ host RingmasterBar).
        paddingBottom: 110 + actionOffset,
        gap: 'var(--space-5)',
        overflow: 'hidden',
      }}
      className="game-screen"
    >
      {/* Violet arcana wisps orbiting while the seer chooses (phone-capped). */}
      <ParticleLayer preset="arcanaWisps" intensity={0.7} slot={0} />

      {/* Header */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
          <Glyph name="seerEye" size={44} tone="arcana" />
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            margin: 0,
            color: 'var(--text-primary)',
            letterSpacing: '0.02em',
          }}
        >
          Whose soul will you read?
        </h1>
        <p style={{ color: 'var(--arcana-4, #c9b6ff)', fontSize: 14, margin: 'var(--space-3) 0 0', opacity: 0.85 }}>
          Choose someone to learn their true nature
        </p>
      </div>

      {/* History journal toggle */}
      {privateView.seerHistory && privateView.seerHistory.length > 0 && (
        <div style={{ position: 'relative', zIndex: 1 }}>
          <button
            type="button"
            className="rm-hover-row"
            onClick={() => setJournalOpen((o) => !o)}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--surface-stroke)',
              borderRadius: 12,
              padding: '10px var(--space-4)',
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-ui)',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Journal ({privateView.seerHistory.length} entries)</span>
            <span>{journalOpen ? '▲' : '▼'}</span>
          </button>
          {journalOpen && (
            <div
              style={{
                marginTop: 4,
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid var(--surface-stroke)',
                borderRadius: 12,
                padding: 'var(--space-3) var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {privateView.seerHistory.map((entry) => (
                <div
                  key={`${entry.round}-${entry.targetId}`}
                  style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-secondary)' }}
                >
                  <span>N{entry.round}: {entry.targetName}</span>
                  <span style={{ color: entry.isWerewolf ? READ_WOLF : READ_CLEAR, fontWeight: 600 }}>
                    {entry.isWerewolf ? 'WOLF' : 'clear'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Candidates */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {candidates.map((id) => {
            const pName = nameMap[id] ?? id;
            const isSelected = selected === id;
            // Mark if already investigated.
            const alreadyKnown = privateView.seerHistory?.find((h) => h.targetId === id);
            return (
              <li key={id}>
                <button
                  type="button"
                  className={isSelected ? undefined : 'rm-hover-row'}
                  onClick={() => setSelected(id)}
                  style={{
                    width: '100%',
                    minHeight: 52,
                    textAlign: 'left',
                    padding: '14px var(--space-4)',
                    borderRadius: 14,
                    border: isSelected
                      ? '1px solid rgba(153,0,0,0.8)'
                      : '1px solid var(--surface-stroke)',
                    background: isSelected
                      ? 'rgba(153,0,0,0.2)'
                      : 'rgba(255,255,255,0.04)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    transition: 'background var(--duration-fast) var(--ease-standard)',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: isSelected ? '2px solid rgba(153,0,0,0.9)' : '2px solid var(--surface-stroke)',
                        background: isSelected ? 'rgba(153,0,0,0.6)' : 'transparent',
                        display: 'inline-block',
                        flexShrink: 0,
                        transition: 'all var(--duration-fast) var(--ease-standard)',
                      }}
                    />
                    {pName}
                  </span>
                  {alreadyKnown && (
                    <span style={{ fontSize: 12, color: alreadyKnown.isWerewolf ? READ_WOLF : READ_CLEAR }}>
                      {alreadyKnown.isWerewolf ? 'WOLF' : 'clear'}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Confirm — fixed at the bottom, always visible */}
      <FixedActionBar offsetBottom={actionOffset}>
        <button
          type="button"
          className="rm-hover"
          disabled={!selected}
          onClick={handleConfirm}
          style={{
            width: '100%',
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            fontSize: 16,
            borderRadius: 'var(--radius-pill)',
            padding: '16px 24px',
            background: selected ? 'linear-gradient(135deg, #802636 0%, #990000 100%)' : 'rgba(255,255,255,0.1)',
            color: selected ? '#ffffff' : 'var(--text-tertiary)',
            border: 'none',
            cursor: selected ? 'pointer' : 'not-allowed',
            minHeight: 52,
            transition: 'all var(--duration-base) var(--ease-standard)',
          }}
        >
          {selected ? `Investigate ${nameMap[selected] ?? selected}` : 'Select someone'}
        </button>
      </FixedActionBar>
    </div>
  );
}
